import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Text } from '../elements/text.element.jsx';
import usePushNotifications from '../../hooks/usePushNotifications.js';

import { Button } from '../elements/Button.jsx';
export const NotificationsBell = ({ unreadCount = 0, items = [], onMarkRead, onRefresh }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const navigate = useNavigate();

  // Close on outside-click / Escape.
  //
  // The panel had neither, so the only way out was to find the bell again and
  // click it a second time — clicking anywhere else on the page left it open
  // over the content you were trying to reach. There is a visible close button
  // in the header below as well; a dismissible overlay should be dismissible
  // three ways, and this one offered none.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Desktop (Web Push) opt-in, through the same hook the notification settings
  // page uses. We NEVER auto-prompt; the permission dialog only appears when the
  // user clicks "Enable".
  const push = usePushNotifications();
  const pushSupported = push.supported;
  // 'granted' with no subscription (after Disable) reads as off, not "On".
  const pushState = push.status === 'granted' && !push.on ? 'default' : push.status;
  const pushBusy = push.busy;
  const [pushHint, setPushHint] = useState('');

  const handleEnablePush = async () => {
    setPushHint('');
    const result = await push.enable();
    if (!result.ok) setPushHint(result.message);
  };

  const handleDisablePush = async () => {
    setPushHint('');
    await push.disable();
  };

  const handleAction = async (item) => {
    const actionUrl = String(item?.content?.actionUrl || item?.metadata?.actionUrl || item?.metadata?.action_url || '').trim();
    if (!actionUrl) return;
    if (!item?.isRead) {
      await onMarkRead?.(item._id);
    }
    setOpen(false);
    navigate(actionUrl);
    await onRefresh?.();
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        className="relative p-2 rounded-full hover:bg-gray-100"
        onClick={() => setOpen((prev) => !prev)}
        aria-label={open ? 'Close notifications' : 'Open notifications'}
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.4-1.4A2 2 0 0 1 18 14.17V11a6 6 0 1 0-12 0v3.17a2 2 0 0 1-.6 1.43L4 17h5m6 0a3 3 0 1 1-6 0h6z" />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-600 text-white text-[10px] px-1.5 py-0.5 rounded-full">
            {unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-background border border-gray-100 rounded-2xl shadow-lg p-4 z-50">
          <div className="flex items-center justify-between gap-2">
            <Text variant="label">Notifications</Text>
            <Button variant="ghost"
              onClick={() => setOpen(false)}
              // "Dismiss", not "Close": the bell itself already announces
              // itself as "Close notifications" while open, and two buttons
              // with the same accessible name is exactly the ambiguity a
              // screen-reader user cannot resolve.
              aria-label="Dismiss notifications">
              <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M18 6L6 18" />
              </svg>
            </Button>
          </div>

          {pushSupported && (
            <div className="mt-3 flex items-center justify-between gap-2 rounded-xl bg-gray-50 border border-gray-100 px-3 py-2">
              <div className="min-w-0">
                <Text variant="body" className="text-[12px] font-semibold text-gray-800 block">Desktop notifications</Text>
                <Text variant="body" className="text-[11px] text-gray-500 block">
                  {pushState === 'granted' && 'On — you’ll be notified when this tab isn’t focused.'}
                  {pushState === 'denied' && 'Blocked in your browser settings.'}
                  {(pushState === 'default' || pushState === 'error') && 'Get notified even when the tab isn’t focused.'}
                  {pushState === 'disabled' && 'Not available right now.'}
                </Text>
              </div>
              {pushState === 'granted' ? (
                <Button variant="outline" size="sm" className="shrink-0 uppercase"
                  disabled={pushBusy}
                  onClick={handleDisablePush}>
                  {pushBusy ? '…' : 'Disable'}
                </Button>
              ) : (
                <Button variant="outline" size="sm" className="shrink-0 uppercase"
                  disabled={pushBusy || pushState === 'denied'}
                  onClick={handleEnablePush}>
                  {pushBusy ? '…' : 'Enable'}
                </Button>
              )}
            </div>
          )}
          {pushHint && (
            <Text variant="body" className="mt-2 text-[11px] text-red-500 block">{pushHint}</Text>
          )}

          <div className="mt-4 space-y-3 max-h-72 overflow-auto">
            {items.length === 0 && (
              <Text variant="body" className="text-gray-500">No notifications yet.</Text>
            )}
            {items.map((item) => (
              <div key={item._id} className="border border-gray-100 rounded-xl p-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <Text variant="body" className="font-semibold text-neutral">{item.title || item.type}</Text>
                    <Text variant="body" className="text-gray-500 block mt-1">{item.body}</Text>
                  </div>
                  {!item.isRead && (
                    <Button variant="ghost" size="sm" className="uppercase"
                     
                      aria-label={`Mark notification ${item.title || item.type || 'item'} as read`}
                      onClick={() => onMarkRead(item._id)}>
                      Mark
                    </Button>
                  )}
                </div>
                {String(item?.content?.actionUrl || item?.metadata?.actionUrl || item?.metadata?.action_url || '').trim() && (
                  <div className="mt-3 flex justify-end">
                    <Button variant="outline" size="sm" className="uppercase"
                     
                      onClick={() => handleAction(item)}>
                      {String(item?.event || '') === 'PURCHASE_CLAIM_READY' ? 'Claim' : 'Open'}
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
